import {
  addPitchBendsToNoteEvents,
  BasicPitch,
  NoteEventTime,
  noteFramesToTime,
  outputToNotesPoly
} from '@playground-sessions/pitch-detection';
import {getBaseUrl} from "../../config/environment";
import {logger} from "../log/logger";
import {deviceStore} from "../../redux/device/deviceStore";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";

export const lowestNoteOnAPiano = 21;
export const highestNoteOnAPiano = 108;

export function convertAudioBufferToMonoChannelAudioBuffer(inputBuffer: AudioBuffer, audioContext: AudioContext) {
  // Create a new empty AudioBuffer with 1 channel (mono) but the same length and sample rate
  const monoBuffer = audioContext.createBuffer(1, inputBuffer.length, inputBuffer.sampleRate);

  // Get the buffer for the single channel of the new mono AudioBuffer
  const monoData = monoBuffer.getChannelData(0);

  // Mix down all channels to mono
  for (let channel = 0; channel < inputBuffer.numberOfChannels; channel++) {
    const inputData = inputBuffer.getChannelData(channel);
    for (let i = 0; i < inputData.length; i++) {
      // Simple average mixdown strategy
      monoData[i] = (monoData[i] || 0) + inputData[i] / inputBuffer.numberOfChannels;
    }
  }

  // monoBuffer now contains the mixed down mono audio
  return monoBuffer;
}

// Use this function to resample your audio buffer to 22050 Hz before passing it to BasicPitch
export async function resampleAudio(audioBuffer: AudioBuffer, targetSampleRate: number): Promise<AudioBuffer> {
  return new Promise((resolve, reject) => {
    try {
      // Create an offline audio context with the target sample rate
      const offlineContext = new OfflineAudioContext(audioBuffer.numberOfChannels, audioBuffer.duration * targetSampleRate, targetSampleRate);

      // Create a buffer source
      const bufferSource = offlineContext.createBufferSource();
      bufferSource.buffer = audioBuffer;

      // Connect the source to the context
      bufferSource.connect(offlineContext.destination);
      bufferSource.start(0);

      // Render the audio at the new sample rate
      offlineContext.startRendering().then((renderedBuffer) => {
        resolve(renderedBuffer);
      });
    } catch (error) {
      console.error('Error resampling audio:', error);
      reject(error);
    }
  });
}

export function midiNumberToNoteName(midiNumber: number): string {
  const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const octave = Math.floor(midiNumber / 12) - 1; // MIDI note 0 is C-1
  const noteIndex = midiNumber % 12;
  const noteName = notes[noteIndex];
  return `${noteName}${octave}`;
}

export async function prepareAudioBufferForTranscription(audioBuffer: AudioBuffer, audioContext: AudioContext): Promise<AudioBuffer> {
  // Transcription requires an AudioBuffer with 1 channel and a sample rate of 22050
  return new Promise((resolve, reject) => {
    try {
      const monoChannelBuffer = convertAudioBufferToMonoChannelAudioBuffer(audioBuffer, audioContext);
      resampleAudio(monoChannelBuffer, 22050).then((resampledBuffer) => {
        resolve(resampledBuffer);
      });
    } catch (error) {
      reject(error);
    }
  });
}

export function mergeAudioBuffers(audioContext: AudioContext, buffer1: AudioBuffer, buffer2: AudioBuffer) {
  const numberOfChannels = Math.min(buffer1.numberOfChannels, buffer2.numberOfChannels);
  const totalLength = buffer1.length + buffer2.length;
  const result = audioContext.createBuffer(numberOfChannels, totalLength, buffer1.sampleRate);

  for (let channel = 0; channel < numberOfChannels; channel++) {
    const channelData = result.getChannelData(channel);
    channelData.set(buffer1.getChannelData(channel), 0);
    channelData.set(buffer2.getChannelData(channel), buffer1.length);
  }

  return result;
}

const isGpuAvailable = async (): Promise<boolean> => {
  if (!navigator.gpu) {
    return false;
  }

  const adapter = await navigator.gpu.requestAdapter();

  if (!adapter) {
    return false;
  }

  try {
    const device = await adapter.requestDevice();
    return !!device;
  } catch (error) {
    return false;
  }
}

/*
  * Transcribe an AudioBuffer to MIDI notes using the BasicPitch model
  * Input AudioBuffer must be mono channel and have a sample rate of 22050
 */
export async function transcribeAudioBufferToMidiNotes(
  inputBuffer: AudioBuffer,
  percentageCompleteCallback: (percent: number) => void = (_percent: number) => {},
  minPitch: number = lowestNoteOnAPiano,
  maxPitch: number = highestNoteOnAPiano
): Promise<NoteEventTime[]> {
  let basicPitch: BasicPitch;
  // In the native app, getDeviceOsIsIOS() is true for both iPhones and iPads.
  const isIos = deviceStore.getDeviceOsIsIOS();

  try {
    const baseUrl = getBaseUrl();
    const hasGpu = await isGpuAvailable();
    const tensorflowBackend = hasGpu && isIos ? 'webgpu' : 'wasm';
    basicPitch = await BasicPitch.init(baseUrl + '/pitchDetection/model.json', tensorflowBackend, '/wasm/');
    console.log('Pitch detection is initialized with TensorFlow backend:', basicPitch.getTensorflowBackend());
    lessonPlayerStore.setLastTensorFlowBackendEngineInitialized(basicPitch.getTensorflowBackend());
  } catch (error) {
    console.error('Error initializing Pitch detection:', error);
    throw error;
  }

  return new Promise((resolve, reject) => {
    // minPitch guard
    if (minPitch < lowestNoteOnAPiano) {
      console.error("transcribeAudioToMidiNotes - minPitch is less than the lowest note on a piano");
      throw "transcribeAudioToMidiNotes - minPitch is less than the lowest note on a piano";
    }

    // maxPitch guard
    if (maxPitch > highestNoteOnAPiano) {
      console.error("transcribeAudioToMidiNotes - maxPitch is greater than the highest note on a piano");
      throw "transcribeAudioToMidiNotes - maxPitch is greater than the highest note on a piano";
    }

    // Mono channel guard
    if (inputBuffer.numberOfChannels > 1) {
      console.error("transcribeAudioToMidiNotes - inputBuffer has more than 1 channel");
      throw "transcribeAudioToMidiNotes - inputBuffer cannot have more than 1 channel";
    }

    // Sample rate guard
    if (inputBuffer.sampleRate !== 22050) {
      console.error("transcribeAudioToMidiNotes - inputBuffer sample rate is not 22050");
      throw "transcribeAudioToMidiNotes - inputBuffer sample rate is not 22050";
    }

    try {
      const frames: number[][] = [];
      const onsets: number[][] = [];
      const contours: number[][] = [];

      basicPitch.evaluateModel(
        inputBuffer as AudioBuffer,
        (f: number[][], o: number[][], c: number[][]) => {
          frames.push(...f);
          onsets.push(...o);
          contours.push(...c);
        },
        percentageCompleteCallback,
      ).then(function () {
        const onsetThreshArg = .25; // minimum amplitude of an onset activation to be considered an onset
        const frameThreshArg = .25; // minimum amplitude of a frame activation for a note to remain "on"
        const minNoteLenArg = 10; // minimum allowed note length in frames
        const inferOnsetsArg = undefined; // if True, add additional onsets when there are large differences in frame amplitudes
        const maxFreqArg = undefined; // maximum allowed output frequency, in Hz
        const minFreqArg = undefined; // minimum allowed output frequency, in Hz
        const melodiaTrickArg = undefined; // remove semitones near a peak
        const energyToleranceArg = undefined; // number of frames allowed to drop below 0

        const notes = noteFramesToTime(
          addPitchBendsToNoteEvents(
            contours,
            outputToNotesPoly(
              frames,
              onsets,
              onsetThreshArg,
              frameThreshArg,
              minNoteLenArg,
              inferOnsetsArg,
              maxFreqArg,
              minFreqArg,
              melodiaTrickArg,
              energyToleranceArg),
          ),
        );

        resolve(notes);

        // const noteSubset = filterNotesByRangeAndRemoveDuplicates(notes, minPitch, maxPitch);
        //
        // console.log("playing actual notes");
        // playbackBuffer(inputBuffer, audioContext).then(() => {
        //   // console.log("playing detected notes");
        //   playbackFromDetectedNotes(noteSubset);
        // });
        //
        // if (noteSubset.length === 0) {
        //   console.log("No notes detected");
        // } else {
        //   console.log(noteSubset.length + " notes detected");
        //   // console.log(noteString);
        // }
      });
    } catch (error) {
      console.error('Error transcribing audio to MIDI notes:', error);
      reject(error);
    }
  });
}

export async function playbackBuffer(buffer: AudioBuffer, audioContext: AudioContext): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      // ---------------- Play what is in the buffer to the speakers ----------------
      const bufferSource = audioContext.createBufferSource();
      bufferSource.buffer = buffer;

      // Connect the buffer source to the context's destination (speakers)
      bufferSource.connect(audioContext.destination);

      const timeToStartPlaying = 0;
      const startOffset = 0;

      logger.pitchDetection.info({ message: "Playback started" });
      bufferSource.start(timeToStartPlaying, startOffset, buffer.duration);

      // Optionally, handle the end of playback
      bufferSource.onended = function () {
        logger.pitchDetection.info({ message: "Playback finished" });
        // You can perform additional actions here, such as releasing resources
        bufferSource.disconnect();
        resolve();
      };
    } catch (error) {
      const e = error as Error;
      logger.pitchDetection.error({ message: "Error playing buffer", error: e});
      reject(error);
    }
  });
}

